import {
  Article,
  ArticleAlternativeContentNumber,
  ArticleResolution,
  ArticleType,
  Category,
  Label,
} from '@wix/answers-api';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { isProduction } from '@wix/wix-run-mode';
import cheerio from 'cheerio';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useContext, useMemo } from 'react';

import { ArticleABTestNotification } from '../../components/ArticleABTestNotification';
import { ArticleContent } from '../../components/ArticleContent';
import { ArticlePreviewNotification } from '../../components/ArticlePreviewNotification';
import { ArticleSideBar } from '../../components/ArticleSidebar';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import {
  EXPERIMENTS,
  LOCALES,
  MOCKED_ROADMAP_LABELS,
  USING_EDITOR_X_SUB_CATEGORY_IDS,
} from '../../constants';
import { Context } from '../../context';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import {
  getArticle,
  getArticleById,
  getLabels,
  getPageSeo,
} from '../../services/answers';
import { getWixDocsUrl } from '../../services/wix-docs-redirect';
import { GetArticleResponse, PageType, HttpStatusCode } from '../../types';
import { useArticlePageBi } from '../../utils/articlePageBI';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { getCategoriesBreadcrumb } from '../../utils/categories';
import { RedirectionError } from '../../utils/extractHttpErrorCode';
import { LoggerNamespaces, createLogger } from '../../utils/logger';
import { shouldRedirectArticle } from '../../utils/redirectToWixDocs';
import { getRoadmapLabels, isRoadmapArticle } from '../../utils/roadmap';
import { isSledUserAgent } from '../../utils/userAgent';
import {
  withServerSideProps,
} from '../../utils/with-server-side-props';
import { isWixelRelated } from '../../utils/wixelCategory';
import { useStore } from '../../zustand/stores';

import css from './Article.module.scss';

export type Step = {
  id: string;
  text: string | null;
  level: string | null;
};

const getSteps = (article: Article) => {
  if (!article.content) {
    return [];
  }
  const $ = cheerio.load(article.content);
  const newSteps = $('[level="2"],[level="3"],[level="4"]')
    .get()
    .filter((step) => step.attribs.id?.length > 0)
    .map((step) => ({
      id: step.attribs.id,
      text: $(step).text(),
      level: step.attribs.level,
    }));

  return newSteps.length > 1 ? newSteps : [];
};

export type ArticlePageProps = {
  relatedArticles: Article[];
  article: Article;
  roadmapLabels: Label[];
  steps: Step[];
};

const ArticlePage: NextPage<ArticlePageProps> = ({
  article,
  relatedArticles,
  roadmapLabels,
  steps,
}: ArticlePageProps) => {
  const { locale } = useRouter();
  const { prePageUrl } = useHistoryContext();
  const { pageTemplateData } = useContext(Context);
  const categoriesTree = useStore((state) => state.categoriesTreeSlice.categoriesTree);
  const { t } = useTranslation();
  const { push } = useRouter();

  const {
    breadCrumbLinkClick,
    homePageClick,
  } = useArticlePageBi(article);

  const showSideBar = article.type === ArticleType.ARTICLE;

  const onBreadCrumbLinkClick = useCallback(
    async (category: Category, clickedItemOrder?: number): Promise<void> =>
      breadCrumbLinkClick(category, clickedItemOrder),
    [breadCrumbLinkClick]
  );

  const isRoadmap =
    article.resolution &&
    article.labels &&
    isRoadmapArticle(article.resolution, article.labels, roadmapLabels);

  const roadmapBreadcrumbs = {
    label: t('roadmap.breadcrumbs'),
    uri: '/roadmap',
  };
  const isPrePageIsRoadmap = prePageUrl?.includes('/roadmap');

  const articleBreadcrumbItem = { id: article.id, label: article.title };
  const categoriesBreadcrumb = getCategoriesBreadcrumb(
    categoriesTree,
    article.categoryId,
    onBreadCrumbLinkClick
  );
  const breadcrumbItems = useMemo(
    () =>
      locale === LOCALES.EN && isRoadmap && isPrePageIsRoadmap
        ? [roadmapBreadcrumbs, articleBreadcrumbItem]
        : [...categoriesBreadcrumb, articleBreadcrumbItem],
    [categoriesTree, article]
  );

  const onHomePageClick = useCallback(
    async (label: string) => {
      if (window) {
        await homePageClick(label);
      }
    },
    [homePageClick]
  );

  const onSelectStatus = async (resolution: ArticleResolution) => {
    await push(`/roadmap?resolution=${resolution}`);
  };

  const isWixelArticle = isWixelRelated(PageType.Article, categoriesTree, pageTemplateData);

  return (
    <>
      <Breadcrumbs
        onHomePageClick={onHomePageClick}
        hideHomePage={isWixelArticle}
        breadcrumbItems={breadcrumbItems}
        shallow={false}
      />
      <ArticleABTestNotification article={article} />
      <ArticlePreviewNotification />
      <Box
        direction="horizontal"
        width="100%"
        align='center'
        className={css.articleWrapper}>
        <ArticleContent
          article={article}
          isRoadmapArticle={isRoadmap && locale === LOCALES.EN}
          readingTimeInMinutes={article.readingTimeInMinutes}
          onClickStatus={onSelectStatus}
          relatedArticles={relatedArticles}
          steps={steps}
        />
        {showSideBar && (
          <Box
            direction="vertical"
            position="relative"
            className={css.rightSideNew}
          >
            <ArticleSideBar
              article={article}
              steps={steps}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export const getServerSideProps = withServerSideProps(
  async ({ req, res, locale, query, isWixEmployee, experiments }) => {
    const logger = createLogger(LoggerNamespaces.Article);
    const { articleTitle, alternativeContent, preview } = query;
    const withAlternativeContent = alternativeContent === 'true';
    let article: Pick<GetArticleResponse, 'article'>['article'];
    let relatedArticles: Pick<GetArticleResponse, 'relatedArticles'>['relatedArticles'];
    const shouldConductVersionB = withAlternativeContent && isWixEmployee;
    const { VERSION_A, VERSION_B } = ArticleAlternativeContentNumber;
    const isArticlePreviewEnabled = experiments[EXPERIMENTS.SPECS.ARTICLE_PREVIEW] === 'true';
    const isPreview = preview === 'true' && isArticlePreviewEnabled;
    try {
      const data = await getArticle(
        req.aspects,
        articleTitle as string,
        locale,
        res.locals.redisClient,
        isPreview,
        (() => {
          if (shouldConductVersionB) {
            return VERSION_B;
          } else if (isWixEmployee) {
            return VERSION_A;
          } else {
            return undefined;
          }
        })()
      );

      article = data.article;
      relatedArticles = data.relatedArticles;
    } catch (error: any) {
      if (error instanceof RedirectionError) {
        return {
          redirect: {
            statusCode: HttpStatusCode.MOVED_PERMANENTLY,
            destination: encodeURI(`/${locale}/article${error.location}`),
          },
        };
      }
      throw error;
    }

    let roadmapLabels: Label[] = [];
    if (article.type === ArticleType.FEATURE_REQUEST) {
      const allLabels = isProduction()
        ? await getLabels(req.aspects, res.locals.redisClient)
        : MOCKED_ROADMAP_LABELS;
      roadmapLabels = getRoadmapLabels(allLabels as Label[]);
    }

    if (USING_EDITOR_X_SUB_CATEGORY_IDS.includes(article.categoryId || '')) {
      // editor x is deprecated. Redirect to migration article.
      logger.info('Diagnostic of redirections of old EditorX articles', { articleUri: articleTitle });
      return {
        redirect: {
          statusCode: 301,
          destination: encodeURI(`/${locale}/article/wix-studio-faqs-transitioning-from-editor-x`),
        },
      };
    }

    if (shouldRedirectArticle(article.id)) {
      const isEnglish = article.locale === LOCALES.EN;

      const redirectArticle = !isEnglish
        ? await getArticleById(req.aspects, article.id as string, LOCALES.EN)
        : article;

      const wixDocsUrl = await getWixDocsUrl(
        res.locals.appContext,
        redirectArticle.url as string
      );

      if (wixDocsUrl) {
        const userAgent = req.headers['user-agent'];
        if (isProduction() && (userAgent && !isSledUserAgent(userAgent))) {
          const logData = {
            from: redirectArticle.url,
            to: wixDocsUrl,
            referrer: req.headers.referer,
          };
          logger.info('Redirecting to Wix Docs', logData);
        }

        return {
          redirect: {
            statusCode: HttpStatusCode.MOVED_PERMANENTLY,
            destination: wixDocsUrl,
          },
        };
      }
    }

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.Article,
      res.locals.redisClient
    );

    return {
      props: {
        relatedArticles,
        article,
        pageData: {
          article: {
            ...article,
            withRelatedArticles:
              !!relatedArticles && relatedArticles.length,
          },
          roadmapData: { roadmapLabels },
        },
        pageType: PageType.Article,
        itemId: article.id,
        itemType: mapArticleTypeToItemType(article?.type),
        pageSeo,
        roadmapLabels,
        steps: getSteps(article as Article),
      }
    };
  }
);

export default ArticlePage;
